import { ArrowRight } from "lucide-react";
import { useState } from "react";

import { Eyebrow } from "@/components/eyebrow";
import { Field } from "@/components/field";
import { NativeSelect } from "@/components/native-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { BUSINESS_MODELS, COMPANY_SIZES, GEOGRAPHIES, ROLES, SECTORS, SUBSECTORS } from "./options";
import { SelectOrOther } from "./select-or-other";
import type { CompanyMeta } from "./types";

interface CompanyBasicsProps {
  meta: CompanyMeta;
  onChange: (meta: CompanyMeta) => void;
  onContinue: () => void;
}

export function CompanyBasics({ meta, onChange, onContinue }: CompanyBasicsProps) {
  const [error, setError] = useState<string | null>(null);
  const set = (key: keyof CompanyMeta, value: string) => onChange({ ...meta, [key]: value });

  function next() {
    if (!meta.company_name.trim()) {
      setError("Company name is required.");
      return;
    }
    if (!meta.sector.trim()) {
      setError("Sector is required.");
      return;
    }
    setError(null);
    onContinue();
  }

  const subsectorOptions = SUBSECTORS[meta.sector] ?? [];

  return (
    <div>
      <Eyebrow>Step 1 of 4 · About you</Eyebrow>
      <h1 className="text-primary mt-3 text-4xl font-normal tracking-tight sm:text-5xl">
        Tell us about the company.
      </h1>
      <p className="text-muted-foreground mt-4 mb-7 text-lg">
        A few basics frame the diagnosis. Next, you can drop in documents to prefill the questions,
        or skip straight to them.
      </p>

      {error && (
        <div className="border-destructive bg-destructive/10 text-destructive mb-6 rounded-md border px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company name *">
          <Input
            type="text"
            value={meta.company_name}
            onChange={(e) => set("company_name", e.target.value)}
          />
        </Field>
        <Field label="Company website" hint="Used later to prefill from your site.">
          <Input
            type="url"
            placeholder="https://"
            value={meta.company_url}
            onChange={(e) => set("company_url", e.target.value)}
          />
        </Field>

        <SelectOrOther
          label="Sector *"
          value={meta.sector}
          options={SECTORS}
          placeholder="Select a sector…"
          // Changing sector clears a now-irrelevant subsector.
          onChange={(v) => onChange({ ...meta, sector: v, subsector: "" })}
        />
        <SelectOrOther
          label="Subsector"
          value={meta.subsector}
          options={subsectorOptions}
          placeholder={meta.sector ? "Select a subsector…" : "Pick a sector first…"}
          onChange={(v) => set("subsector", v)}
        />

        <SelectOrOther
          label="Geography"
          value={meta.geography}
          options={GEOGRAPHIES}
          placeholder="Select a region…"
          onChange={(v) => set("geography", v)}
        />
        <Field label="Company size" hint="By headcount (number of employees).">
          <NativeSelect
            value={meta.company_size}
            onChange={(e) => set("company_size", e.target.value)}
          >
            {COMPANY_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </NativeSelect>
        </Field>

        <SelectOrOther
          label="Business model type"
          value={meta.business_model_type}
          options={BUSINESS_MODELS}
          placeholder="Select a business model…"
          onChange={(v) => set("business_model_type", v)}
        />
        <SelectOrOther
          label="Your role"
          value={meta.respondent_role}
          options={ROLES}
          placeholder="Select your role…"
          onChange={(v) => set("respondent_role", v)}
        />
      </div>

      <div className="mt-5">
        <Field
          label="Context (optional)"
          hint="Strategic priorities, current AI initiatives, known pain points. Adds confidence to the diagnosis."
        >
          <Textarea
            className="bg-card min-h-24"
            value={meta.free_text_context}
            onChange={(e) => set("free_text_context", e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <span />
        <Button onClick={next}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
